import { useEffect, useState } from "react";
import { Loader2, Save, Palette, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { useTenant } from "@/contexts/TenantContext";

export default function BrandingPage() {
  const { refresh } = useTenant();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const { canWrite } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try { const r = await api.get("/tenants/me"); setTenant(r.data); setForm({ ...(r.data?.branding || {}) }); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!tenant?.id) return;
    setSaving(true);
    try {
      await api.patch(`/tenants/${tenant.id}`, { branding: form });
      toast.success("Branding updated"); load(); refresh && refresh();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }

  async function onLogoFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Please choose an image file."); return; }
    if (file.size > 1024 * 1024) { toast.error("Logo must be under 1 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => { setForm((f) => ({ ...f, logo_url: reader.result })); toast.success("Logo loaded — click Save to apply."); };
    reader.onerror = () => toast.error("Could not read the image.");
    reader.readAsDataURL(file);
  }

  async function onWordmarkFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Please choose an image file."); return; }
    if (file.size > 1024 * 1024) { toast.error("Logo must be under 1 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => { setForm((f) => ({ ...f, logo_wordmark_url: reader.result })); toast.success("Full logo loaded — click Save to apply."); };
    reader.onerror = () => toast.error("Could not read the image.");
    reader.readAsDataURL(file);
  }

  if (loading) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="px-4 sm:px-6 py-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="branding-title">Branding & White-Label</h1>
          <p className="text-sm text-muted-foreground mt-1">Customize logo, colors, fonts. Applied to login + agent UI for this tenant.</p>
        </div>
        {canWrite && <Button data-testid="branding-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Save
        </Button>}
      </div>
      <Card className="p-5 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Logo mark (C7 icon)</Label>
            <p className="text-[11px] text-muted-foreground mb-1.5">Small square icon — used on the login left panel &amp; browser favicon.</p>
            <div className="flex items-center gap-3">
              {form.logo_url
                ? <img src={form.logo_url} alt="logo preview" className="h-12 w-12 rounded-md bg-white object-contain p-1 border" data-testid="b-logo-preview" />
                : <div className="h-12 w-12 rounded-md border border-dashed flex items-center justify-center text-muted-foreground"><Palette className="w-5 h-5" /></div>}
              <div className="flex-1 space-y-2">
                <Input data-testid="b-logo" value={(form.logo_url||"").startsWith("data:") ? "" : (form.logo_url||"")} onChange={(e)=>setForm({...form, logo_url:e.target.value})} placeholder="https://… or upload an image →" />
                <div className="flex items-center gap-2">
                  <label htmlFor="logo-upload" className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border cursor-pointer hover:bg-muted transition" data-testid="b-logo-upload-btn">
                    <Upload className="w-3.5 h-3.5" /> Upload image
                  </label>
                  <input id="logo-upload" type="file" accept="image/*" className="hidden" onChange={onLogoFile} data-testid="b-logo-file" />
                  {form.logo_url && <button type="button" className="text-xs text-rose-600 hover:underline" data-testid="b-logo-clear" onClick={()=>setForm({...form, logo_url:""})}>Remove</button>}
                  <span className="text-[11px] text-muted-foreground">PNG/SVG/JPG, &lt; 1 MB</span>
                </div>
              </div>
            </div>
          </div>

          <div className="sm:col-span-2">
            <Label>Full logo (Connect7 by Haloocom)</Label>
            <p className="text-[11px] text-muted-foreground mb-1.5">Wide wordmark — shown on the login right panel &amp; in the agent / admin headers.</p>
            <div className="flex items-center gap-3">
              {form.logo_wordmark_url
                ? <img src={form.logo_wordmark_url} alt="wordmark preview" className="h-12 w-auto max-w-[180px] rounded-md bg-white object-contain p-1 border" data-testid="b-wordmark-preview" />
                : <div className="h-12 w-28 rounded-md border border-dashed flex items-center justify-center text-muted-foreground"><Palette className="w-5 h-5" /></div>}
              <div className="flex-1 space-y-2">
                <Input data-testid="b-wordmark" value={(form.logo_wordmark_url||"").startsWith("data:") ? "" : (form.logo_wordmark_url||"")} onChange={(e)=>setForm({...form, logo_wordmark_url:e.target.value})} placeholder="https://… or upload an image →" />
                <div className="flex items-center gap-2">
                  <label htmlFor="wordmark-upload" className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border cursor-pointer hover:bg-muted transition" data-testid="b-wordmark-upload-btn">
                    <Upload className="w-3.5 h-3.5" /> Upload image
                  </label>
                  <input id="wordmark-upload" type="file" accept="image/*" className="hidden" onChange={onWordmarkFile} data-testid="b-wordmark-file" />
                  {form.logo_wordmark_url && <button type="button" className="text-xs text-rose-600 hover:underline" data-testid="b-wordmark-clear" onClick={()=>setForm({...form, logo_wordmark_url:""})}>Remove</button>}
                  <span className="text-[11px] text-muted-foreground">Transparent PNG preferred, &lt; 1 MB</span>
                </div>
              </div>
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label>Brand / primary color — all surfaces</Label>
            <p className="text-[11px] text-muted-foreground mb-1.5">Premium primary/accent color used across <strong>Login</strong>, the <strong>Admin console</strong>, and the <strong>Agent workspace</strong> (buttons, toggles, active nav).</p>
            <div className="flex items-center gap-2">
              <Input data-testid="b-button-color" value={form.admin_button_color||""} onChange={(e)=>setForm({...form, admin_button_color:e.target.value})} placeholder="#1E40AF" />
              <div className="w-9 h-9 rounded-md border shrink-0" style={{ background: form.admin_button_color || "#1E40AF" }} data-testid="b-button-color-swatch" />
              <input type="color" data-testid="b-button-color-picker" value={/^#[0-9a-fA-F]{6}$/.test(form.admin_button_color||"") ? form.admin_button_color : "#1E40AF"} onChange={(e)=>setForm({...form, admin_button_color:e.target.value})} className="w-9 h-9 rounded-md border cursor-pointer bg-transparent" />
            </div>
          </div>
          <div><Label>Display font</Label><Input data-testid="b-font-display" value={form.font_display||""} onChange={(e)=>setForm({...form, font_display:e.target.value})} placeholder="Chivo" /></div>
          <div><Label>Body font</Label><Input data-testid="b-font-body" value={form.font_body||""} onChange={(e)=>setForm({...form, font_body:e.target.value})} placeholder="IBM Plex Sans" /></div>
          <div className="flex items-center gap-2 sm:col-span-2"><Switch data-testid="b-white-label" checked={!!form.white_label} onCheckedChange={(v)=>setForm({...form, white_label:!!v})} /><Label>White-label (hide "Powered by Connect7")</Label></div>
        </div>

        <div className="border-t pt-5">
          <div className="flex items-center gap-2 mb-3"><Palette className="w-4 h-4 text-primary"/><h2 className="font-display font-bold">Live preview</h2></div>
          <div className="rounded-lg border p-5 bg-card">
            <div className="flex items-center gap-3">
              {form.logo_url && <img src={form.logo_url} alt="" className="h-10 w-10 rounded-md bg-white p-1 border" />}
              <div>
                <div className="font-bold text-lg text-foreground" style={{ fontFamily: form.font_display || "Chivo" }}>Connect7 — {tenant?.name}</div>
                <div className="text-xs text-muted-foreground" style={{ fontFamily: form.font_body || "IBM Plex Sans" }}>Sample heading and body using your selected fonts.</div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button type="button" className="px-3 py-1.5 rounded-md text-xs font-semibold text-white" style={{ background: form.admin_button_color || "#1E40AF" }} data-testid="preview-primary-btn">Primary button</button>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">The <strong>Primary button</strong> uses your brand color and appears on <strong>Login</strong>, <strong>Admin</strong> &amp; the <strong>Agent workspace</strong> (buttons, toggles, active nav, links).</p>
        </div>
      </Card>
    </div>
  );
}
